/**
 * The loyalty dashboard: tier progress, streaks, challenges, badges and the leaderboard, the four
 * modals they open, and the two that take the fan back out of the programme.
 *
 * LoyaltyInfo only reports the clicks — this layer owns which modal is open, which is the
 * library's event-prop composition pattern. Ported from apps/demo/src/InfoWithModals.tsx.
 */

import { useCallback, useEffect, useMemo, useState } from 'preact/hooks';
import {
	BadgesModal,
	ChallengesModal,
	LeaderboardModal,
	LeaveLoyaltyModal,
	LoyaltyInfo,
	ManageLoyaltyModal,
	ProfileOverviewModal,
	useLoyaltyClient,
	usePrefetch,
} from '@drewling/urc-loyalty-ui';
import { resolveBadgeIcon } from '../badgeArtwork';
import { parseRichText, splitConsequences } from '../membershipCopy';
import { useLeaveLoyalty } from '../useLeaveLoyalty';

// Each fetcher calls the SAME method as its modal's body, so the prefetch cache — keyed by method
// name — is warmed under the exact key the modal reads back. Hoisted for a stable identity.
const fetchChallenges = ( api, opts ) => api.getChallenges( opts );
const fetchBadgesDetail = ( api, opts ) => api.getBadgesDetail( opts );
const fetchLeaderboardDetail = ( api, opts ) => api.getLeaderboardDetail( opts );
const fetchProfileStats = ( api, opts ) => api.getProfileStats( opts );

/*
 * The profile overview's MOTM and Polls sections are veiled: 3Halves can currently serve wrong
 * voting figures, so both draw the library's blurred "coming soon" state, which puts placeholder
 * figures under the blur rather than the fan's. Set a section back to 'live' to show its numbers
 * again. The prop is REQUIRED from uxlib 0.21.0 — without it the Stats tab falls to "Couldn't load
 * stats", and nothing in this untyped build would say why.
 */
const PROFILE_STAT_SECTIONS = { motm: 'comingSoon', polls: 'comingSoon' };

/**
 * @param {Object}  props
 * @param {Object=} props.labels            Editor copy: `{ tierProgress, manage, leave }`.
 * @param {string=} props.leaveRedirectUrl  Where a fan who has left is sent. No URL, no way out.
 * @param {string=} props.consentLinkUrl    Target of the consent row's link, if there is one.
 * @param {string=} props.consentLinkTarget '_blank' when the editor asked for a new tab.
 */
export function ProfileBlock( {
	labels = {},
	leaveRedirectUrl,
	consentLinkUrl,
	consentLinkTarget,
} ) {
	const [ open, setOpen ] = useState( null );
	const close = () => setOpen( null );

	// Hover or focus a trigger and its modal's data is already in flight by click-time.
	const prefetchChallenges = usePrefetch( fetchChallenges );
	const prefetchBadges = usePrefetch( fetchBadgesDetail );
	const prefetchLeaderboard = usePrefetch( fetchLeaderboardDetail );
	const prefetchProfileOverview = usePrefetch( fetchProfileStats );

	const manageLabels = labels.manage || {};
	const leaveLabels = labels.leave || {};
	const settings = ( typeof window !== 'undefined' && window.urcLoyalty?.consent ) || {};

	/*
	 * Whether the fan is offered a way out at all.
	 *
	 * Without a destination there is nowhere to send them and staying put is the worst outcome
	 * available: the consent write is cached the moment it lands, so every card on this page would
	 * redraw as a refusal. So the whole surface is withheld — and the hook is TOLD, rather than
	 * working it out from a prop it does not have, so that it makes no reads at all for a dialog
	 * that can never open. This component runs on every profile page, and one of those reads is not
	 * in the prefetch memo.
	 */
	const offered = Boolean( leaveRedirectUrl );

	// Both groups, each under its own name. The hook composes the leave dialog's sentences AND the
	// manage dialog's membership card, and handing it one bag called `labels` is how the card's
	// points unit came to be read off the leave group, where it has never lived.
	const { membership, body, phase, reset, submit } = useLeaveLoyalty( {
		leaveLabels,
		manageLabels,
		settings,
		offered,
	} );

	/*
	 * Start over when the fan does.
	 *
	 * ../../loyalty.jsx hands every mounted block a NEW client on an account switch and repaints it
	 * in place — the same component, so Preact diffs rather than remounts and this state would
	 * otherwise survive the change of fan. An open dialog is the case that matters: it would be
	 * showing the previous fan's tier and points, over a Leave button wired to whoever is signed in
	 * now.
	 */
	const client = useLoyaltyClient();

	useEffect( () => {
		setOpen( null );
		reset();
	}, [ client, reset ] );

	/*
	 * The consequences list and the withdrawal notice, parsed once.
	 *
	 * Both arrive as authored text, and both carry a rule that comes from the library's own
	 * renderer rather than from taste: the list's lines are their own React keys, so duplicates are
	 * dropped, and the notice's runs are concatenated with nothing between them, so nothing is
	 * trimmed. ../membershipCopy.js is where both are enforced and tested.
	 */
	const consequences = useMemo(
		() => splitConsequences( manageLabels.consequences ),
		[ manageLabels.consequences ]
	);
	const withdrawalNotice = useMemo(
		() => parseRichText( manageLabels.withdrawalNotice ),
		[ manageLabels.withdrawalNotice ]
	);

	/*
	 * The recorded-consent row, or nothing.
	 *
	 * All or nothing, and that is the library's shape rather than a preference: ManageLoyaltyModal
	 * renders its link affordance unconditionally inside the `consent !== undefined` branch, so a
	 * row carrying a blank label ships an empty element instead of a tidy sentence. PHP omits both
	 * keys when the editor leaves them blank, so an absent one here is the whole row's absence.
	 */
	const consent = useMemo( () => {
		if ( ! manageLabels.consentText || ! manageLabels.consentLinkLabel ) {
			return undefined;
		}

		const row = { text: manageLabels.consentText, linkLabel: manageLabels.consentLinkLabel };

		if ( consentLinkUrl ) {
			row.linkHref = consentLinkUrl;

			// The modal renders the anchor itself and takes no `target`, so "open in a new tab"
			// has to be honoured here — and it is worth honouring: the fan is part-way through a
			// decision, and following the link in this tab discards the dialog. Same shape as
			// JoinBlock's onTermsClick.
			if ( consentLinkTarget === '_blank' ) {
				row.onLinkClick = event => {
					event.preventDefault();
					window.open( consentLinkUrl, '_blank', 'noopener' );
				};
			}
		}

		return row;
	}, [
		manageLabels.consentText,
		manageLabels.consentLinkLabel,
		consentLinkUrl,
		consentLinkTarget,
	] );

	const leaving = phase === 'leaving';
	const failed = phase === 'failed';

	const startLeaving = useCallback( () => {
		if ( ! leaving ) {
			submit( leaveRedirectUrl );
		}
	}, [ leaving, submit, leaveRedirectUrl ] );

	// The second condition on the trigger: `ManageLoyaltyModal.membership` is required, and drawing
	// a card with a placeholder tier on the surface that asks a fan to give it up is not a trade
	// worth making for a moment's latency.
	const canLeave = offered && membership !== undefined;

	return (
		<>
			<LoyaltyInfo
				labels={ labels.tierProgress ? { tierProgress: labels.tierProgress } : undefined }
				onProfileOverviewClick={ () => setOpen( 'profileOverview' ) }
				onProfileOverviewPrefetch={ prefetchProfileOverview }
				// Never the `…Href` twin: that renders an anchor, and this opens a dialog. Passing
				// it is also what makes the tier card draw its second footer link and the dot that
				// belongs to the pair.
				onManageLoyaltyClick={ canLeave ? () => setOpen( 'manageLoyalty' ) : undefined }
				onChallengesClick={ () => setOpen( 'challenges' ) }
				onChallengesPrefetch={ prefetchChallenges }
				onBadgesClick={ () => setOpen( 'badges' ) }
				onBadgesPrefetch={ prefetchBadges }
				// Earned art for an earned badge, locked art for the rest, the glyph where the API
				// has neither. Same rule in the modal and the toast; see ../badgeArtwork.js.
				resolveBadgeIcon={ resolveBadgeIcon }
				onLeaderboardClick={ () => setOpen( 'leaderboard' ) }
				onLeaderboardPrefetch={ prefetchLeaderboard }
			/>
			<ProfileOverviewModal
				open={ open === 'profileOverview' }
				onClose={ close }
				initialTab="stats"
				statSections={ PROFILE_STAT_SECTIONS }
			/>
			<ChallengesModal open={ open === 'challenges' } onClose={ close } />
			<BadgesModal
				open={ open === 'badges' }
				onClose={ close }
				resolveBadgeIcon={ resolveBadgeIcon }
			/>
			<LeaderboardModal
				open={ open === 'leaderboard' }
				onClose={ close }
				initialScope="matchweek"
			/>
			{ /*
			 * The membership pair, and they are rendered UNCONDITIONALLY rather than behind
			 * `canLeave`, driven only by `open`.
			 *
			 * The handover between them has to be ONE setOpen. Rendering the second conditionally
			 * would unmount the first in the same commit, and React runs every layout-effect
			 * CLEANUP before every new layout effect — so the overlay stack's page lock would be
			 * released, unlocking the page and bouncing focus back to the tier card, and
			 * immediately re-taken, with no exit fade at all. Both panels mounted and one state
			 * value makes a two-panel steady state unrepresentable, which is the same reason
			 * ../useJoinFunnel.js holds one `phase` for the join dialog.
			 */ }
			<ManageLoyaltyModal
				open={ open === 'manageLoyalty' }
				onClose={ close }
				title={ manageLabels.title }
				closeLabel={ manageLabels.closeLabel }
				membership={ membership }
				consent={ consent }
				consequencesHeading={ manageLabels.consequencesHeading }
				consequences={ consequences }
				withdrawalNotice={ withdrawalNotice }
				actions={ [
					{
						id: 'keep',
						label: manageLabels.keepLabel,
						tone: 'primary',
						onClick: close,
					},
					{
						id: 'leave',
						label: manageLabels.leaveLabel,
						tone: 'secondary',
						onClick: () => setOpen( 'leaveLoyalty' ),
					},
				] }
			/>
			<LeaveLoyaltyModal
				open={ open === 'leaveLoyalty' }
				// A no-op while the write is in flight: the X, Escape and the backdrop all route
				// here, and dismissing a write in progress would leave the fan never learning
				// whether they had left. ../leaveLoyalty.js bounds that wait so this cannot trap
				// them.
				onClose={ leaving ? () => {} : close }
				title={ leaveLabels.title }
				closeLabel={ leaveLabels.closeLabel }
				body={ failed ? leaveLabels.errorBody : body }
				// Dropped on a failure. A line promising a confirmation, under a message saying it
				// did not work, is a lie told in a friendly colour.
				confirmationNote={ failed ? undefined : leaveLabels.confirmationNote }
				actions={ [
					{
						id: 'leave',
						// The whole of the in-flight affordance. `ModalActions` hardcodes
						// `disabled: false` on the control it draws, so there is no disabled state
						// to reach for and a label is the honest maximum.
						label: labelFor( leaveLabels, leaving, failed ),
						tone: 'secondary',
						onClick: startLeaving,
					},
					{
						id: 'keep',
						label: leaveLabels.keepLabel,
						tone: 'primary',
						// Out, not back to the manage panel: "keep" is the decision, and
						// reopening the first step of leaving would ask a fan who has just said no
						// to say it again. Inert while the write runs, for the same reason onClose
						// is.
						onClick: leaving ? () => {} : close,
					},
				] }
			/>
		</>
	);
}

/**
 * What the leave button says right now.
 *
 * @param {Object}  leaveLabels The `leave` label group. Named for its group rather than `copy`, so
 *                              that one identifier means one group everywhere in this bundle and
 *                              ./membershipLabels.test.js can check every read against the PHP map.
 * @param {boolean} leaving     Whether a write is in flight.
 * @param {boolean} failed      Whether the last attempt did not land.
 * @return {string} The label.
 */
function labelFor( leaveLabels, leaving, failed ) {
	if ( leaving ) {
		return leaveLabels.busyLabel;
	}

	return failed ? leaveLabels.retryLabel : leaveLabels.confirmLabel;
}
